import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Container } from '../components/layout/Primitives';
import { Button } from '../components/ui/Button';
import { ProductCard } from '../components/commerce/ProductCard';
import {
  ProductCatalogWide,
  ProductCardsScrollOuter,
  ProductCardsStrip,
  ProductCardShell,
  ProductCardsGrid,
} from '../components/commerce/ProductCardLayout';
import { siteImagery } from '../config/siteImagery';
import jl1 from '../assets/jl-1.jpg';
import jl2 from '../assets/jl-2.jpg';
import jl3 from '../assets/jl-3.jpg';
import jl4 from '../assets/jl-4.jpg';
import jl5 from '../assets/jl-5.jpg';
import jl6 from '../assets/jl-6.jpg';
import jl7 from '../assets/jl-7.jpg';
import jl8 from '../assets/jl-8.jpg';
import { isApiEnabled } from '../config/env';
import { modamaiTrace } from '../utils/modamaiTrace';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchHomeCatalog } from '../store/actions/productActions';
import HomeReviewsSection from '../components/reviews/HomeReviewsSection';
import PageSeo from '../components/seo/PageSeo';

const PromoLink = styled(Link)`
  display: block;
  background-image: ${({ theme }) => theme.gradients.gold};
  color: ${({ theme }) => theme.colors.noir};
  &:hover {
    filter: brightness(1.05);
  }
`;

const PromoInner = styled(Container)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  text-align: center;
  padding: 0.75rem 1.5rem;

  p {
    margin: 0;
    font-size: ${({ theme }) => theme.type.bodySm};
  }

  span:first-of-type,
  span:last-of-type {
    font-family: ${({ theme }) => theme.fonts.display};
    font-size: clamp(1.25rem, 3vw, 1.75rem);
  }
`;

/** Light editorial hero — Maison-style split; keeps same campaign image (`heroMain`). */
const HeroSection = styled.section`
  position: relative;
  background: ${({ theme }) => theme.colors.cream};
  overflow: hidden;
`;

const HeroLayout = styled.div`
  display: flex;
  flex-direction: column;
  min-height: clamp(520px, 72vh, 880px);

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: row;
    min-height: clamp(560px, 78vh, 920px);
  }
`;

const HeroCopyPanel = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2.5rem ${({ theme }) => theme.layout.containerPadX};

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    flex: none;
    width: 50%;
    padding: 3rem clamp(24px, 4vw, 48px);
  }
`;

const HeroCopyInner = styled.div`
  text-align: center;
  width: 100%;
  max-width: min(36rem, 92vw);
`;

const HeroEyebrow = styled.p`
  margin: 0 0 clamp(14px, 2.2vw, 22px);
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: ${({ theme }) => theme.type.bodySm};
  font-weight: 500;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.mutedAccent};
`;

const HeroTitle = styled.h1`
  margin: 0 0 clamp(12px, 2vw, 20px);
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: ${({ theme }) => theme.type.heroDisplay};
  font-weight: 300;
  line-height: 1.08;
  color: ${({ theme }) => theme.colors.foreground};
`;

const HeroAccent = styled.span`
  font-style: italic;
  font-weight: 300;
  color: ${({ theme }) => theme.colors.mutedAccent};
`;

const HeroLead = styled.p`
  margin: 0 0 8px;
  font-size: ${({ theme }) => theme.type.body};
  line-height: 1.65;
  color: hsla(25, 8%, 12%, 0.72);
`;

const HeroSubline = styled.p`
  margin: 0 0 clamp(26px, 4vw, 36px);
  font-size: ${({ theme }) => theme.type.bodySm};
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-weight: 500;
  color: hsla(25, 8%, 12%, 0.5);
`;

const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
`;

const HeroFigure = styled.div`
  flex: 1;
  position: relative;
  min-height: 44vh;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    flex: none;
    width: 50%;
    min-height: 0;
    align-self: stretch;
  }
`;

const HeroCampaignImg = styled.img`
  width: 100%;
  height: 100%;
  min-height: 44vh;
  object-fit: cover;
  display: block;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    min-height: 100%;
    position: absolute;
    inset: 0;
  }
`;

const CatStrip = styled.section`
  padding: 2.5rem 0;
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding: 3.5rem 0;
  }
`;

const CatGrid = styled(Container)`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(5, 1fr);
    gap: 1.25rem;
  }
`;

const CatTile = styled(Link)`
  display: block;
  text-align: center;
`;

const CatImgWrap = styled.div`
  aspect-ratio: 1;
  overflow: hidden;
  margin-bottom: 0.75rem;
  background: ${({ theme }) => theme.colors.secondary};
`;

const CatImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.7s ${({ theme }) => theme.easeLuxe};
  ${CatTile}:hover & {
    transform: scale(1.05);
  }
`;

const CatLabel = styled.p`
  margin: 0;
  font-size: 11px;
  font-family: ${({ theme }) => theme.fonts.sans};
  letter-spacing: 0.25em;
  text-transform: uppercase;
  font-weight: 500;
  ${CatTile}:hover & {
    color: ${({ theme }) => theme.colors.champagneDark};
  }
`;

const CarouselSection = styled.section`
  padding-bottom: 3rem;
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding-bottom: 4rem;
  }
`;

const CarouselHead = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: ${({ theme }) => theme.type.sectionTitle};
  font-weight: 300;
  line-height: 1.1;
`;

const SectionKicker = styled.p`
  margin: 0.5rem 0 0;
  font-size: ${({ theme }) => theme.type.bodySm};
  letter-spacing: 0.2em;
  text-transform: uppercase;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const NavIconBtn = styled.button`
  width: 2.5rem;
  height: 2.5rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &:hover {
    border-color: ${({ theme }) => theme.colors.noir};
  }
`;

const FreshDropsSection = styled.section`
  padding: 3.5rem 0 4.25rem;
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding: 4.75rem 0 5.25rem;
  }
`;

const FreshDropsHead = styled(Container)`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const FreshDropsGrid = styled(Container)`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 1.25rem;
  }
`;

const FreshDropCard = styled(Link)`
  position: relative;
  display: block;
  aspect-ratio: 3 / 4;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.secondary};
`;

const FreshDropImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 1.4s ${({ theme }) => theme.easeLuxe};
  ${FreshDropCard}:hover & {
    transform: scale(1.05);
  }
`;

const FreshDropOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, hsla(0, 0%, 0%, 0.78), hsla(0, 0%, 0%, 0.12), transparent);
`;

const FreshDropCopy = styled.div`
  position: absolute;
  inset-inline: 0;
  bottom: 0;
  padding: 1rem 1rem 1.1rem;
  color: ${({ theme }) => theme.colors.cream};
`;

const FreshDropEyebrow = styled.p`
  margin: 0 0 0.45rem;
  font-size: 10px;
  font-family: ${({ theme }) => theme.fonts.sans};
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.champagneLight};
`;

const FreshDropTitle = styled.h3`
  margin: 0;
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(1.1rem, 2.2vw, 1.6rem);
  font-weight: 300;
  line-height: 1.15;
`;

export default function Home() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const storeName = useAppSelector((s) => s.storeSettings?.settings?.name?.trim?.() || '');
  const storeDesc = useAppSelector((s) => s.storeSettings?.settings?.description?.trim?.() || '');
  const homeRail = useAppSelector((s) => s.product?.homeRail ?? []);
  const homeGrid = useAppSelector((s) => s.product?.homeGrid ?? []);
  const homeLoading = useAppSelector((s) => s.product?.homeLoading ?? false);
  const homeError = useAppSelector((s) => s.product?.homeError ?? null);

  useEffect(() => {
    if (!isApiEnabled()) {
      modamaiTrace('Home.jsx skip fetchHomeCatalog (API off)', {});
      return;
    }
    modamaiTrace('Home.jsx dispatch fetchHomeCatalog', {});
    dispatch(fetchHomeCatalog());
  }, [dispatch]);

  useEffect(() => {
    modamaiTrace('Home.jsx product slice snapshot', {
      homeRailLen: homeRail.length,
      homeGridLen: homeGrid.length,
      homeLoading,
      homeError,
    });
  }, [homeRail.length, homeGrid.length, homeLoading, homeError]);

  const apiOn = isApiEnabled();
  const railProducts = apiOn ? homeRail : [];
  const gridProducts = apiOn ? homeGrid : [];

  const carouselRef = useRef(null);

  const scroll = (dir) => {
    const el = carouselRef.current;
    if (!el) return;
    el.scrollBy({
      left: dir === 'left' ? -el.clientWidth * 0.8 : el.clientWidth * 0.8,
      behavior: 'smooth',
    });
  };

  const {
    heroMain,
    catWomen,
    catMen,
    catResort,
    catAccessories,
  } = siteImagery;

  const brand = storeName || t('modamai.product.siteTitle');
  const plainStoreDesc = storeDesc
    ? String(storeDesc)
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    : '';
  const homeDescription = plainStoreDesc || t('modamai.home.seoDescriptionFallback');

  return (
    <div className="animate-fade-in">
      <PageSeo
        documentTitle={t('modamai.home.seoDocumentTitle', { brand })}
        description={homeDescription.slice(0, 320)}
        keywords={t('modamai.home.seoKeywords')}
        canonicalPath="/"
      />
      <PromoLink to="/shop/sale">
        <PromoInner>
          <span>★</span>
          <p style={{ letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 500 }}>
            {t('modamai.home.promoLine')}
          </p>
          <span>★</span>
        </PromoInner>
      </PromoLink>

      <HeroSection>
        <HeroLayout>
          <HeroCopyPanel>
            <HeroCopyInner>
              <HeroEyebrow>{t('modamai.home.seasonEyebrow')}</HeroEyebrow>
              <HeroTitle>
                {t('modamai.home.heroTitleBefore')} <HeroAccent>{t('modamai.home.heroAccent')}</HeroAccent>
              </HeroTitle>
              <HeroLead>{t('modamai.home.heroLead')}</HeroLead>
              <HeroSubline>{t('modamai.home.heroSubline')}</HeroSubline>
              <HeroActions>
                <Button as={Link} to="/shop/new" $variant="luxe" $size="lg">
                  {t('modamai.home.ctaDiscoverNewIn')}
                </Button>
                <Button as={Link} to="/shop/women" $variant="outlineLuxe" $size="default">
                  {t('modamai.home.ctaWomen')}
                </Button>
                <Button as={Link} to="/shop/men" $variant="outlineLuxe" $size="default">
                  {t('modamai.home.ctaMen')}
                </Button>
              </HeroActions>
            </HeroCopyInner>
          </HeroCopyPanel>
          <HeroFigure>
            <HeroCampaignImg src={heroMain} alt={t('modamai.home.heroCampaignAlt')} loading="eager" />
          </HeroFigure>
        </HeroLayout>
      </HeroSection>

      <CatStrip>
        <CatGrid>
          {[
            { img: catWomen, lk: 'catWomen', to: '/shop/women' },
            { img: catMen, lk: 'catMen', to: '/shop/men' },
            { img: catResort, lk: 'catKnitwear', to: '/shop/knitwear' },
            { img: catAccessories, lk: 'catAccessories', to: '/shop/accessories' },
          ].map((c) => (
            <CatTile key={c.to} to={c.to}>
              <CatImgWrap>
                <CatImg src={c.img} alt={t(`modamai.home.${c.lk}`)} loading="lazy" />
              </CatImgWrap>
              <CatLabel>{t(`modamai.home.${c.lk}`)}</CatLabel>
            </CatTile>
          ))}
        </CatGrid>
      </CatStrip>

      <CarouselSection>
        <ProductCatalogWide>
          <CarouselHead>
            <div>
              <SectionTitle style={{ margin: 0 }}>{t('modamai.home.carouselTitle')}</SectionTitle>
              <SectionKicker>{t('modamai.home.carouselSub')}</SectionKicker>
              {apiOn && homeError && (
                <p style={{ margin: '0.75rem 0 0', fontSize: 13, color: '#7a7268' }}>{String(homeError)}</p>
              )}
              {apiOn && homeLoading && railProducts.length === 0 && (
                <p style={{ margin: '0.75rem 0 0', fontSize: 13, color: '#7a7268' }}>{t('modamai.home.loadingPicks')}</p>
              )}
            </div>
            <div style={{ display: 'none', gap: '0.5rem' }} className="sm-flex">
              <style>{`@media(min-width:640px){.sm-flex{display:flex!important}}`}</style>
              <NavIconBtn type="button" aria-label={t('modamai.home.carouselPrev')} onClick={() => scroll('left')}>
                <ChevronLeft size={16} />
              </NavIconBtn>
              <NavIconBtn type="button" aria-label={t('modamai.home.carouselNext')} onClick={() => scroll('right')}>
                <ChevronRight size={16} />
              </NavIconBtn>
            </div>
          </CarouselHead>
          <ProductCardsScrollOuter ref={carouselRef}>
            <ProductCardsStrip>
              {railProducts.map((p) => (
                <ProductCardShell key={`${p.slug || p.id}-rail`}>
                  <ProductCard product={p} />
                </ProductCardShell>
              ))}
            </ProductCardsStrip>
          </ProductCardsScrollOuter>
        </ProductCatalogWide>
      </CarouselSection>

      <FreshDropsSection>
        <FreshDropsHead>
          <div>
            <SectionKicker style={{ marginTop: 0, marginBottom: '0.45rem', color: '#7a7268' }}>
              {t('modamai.home.freshKicker')}
            </SectionKicker>
            <SectionTitle style={{ margin: 0 }}>{t('modamai.home.freshTitle')}</SectionTitle>
          </div>
          <Link
            to="/shop/new"
            className="font-mono-luxe underline-luxe"
            style={{ fontSize: '11px', letterSpacing: '0.2em', textTransform: 'uppercase' }}
          >
            {t('modamai.home.viewAllNewIn')} <ArrowRight size={12} style={{ marginLeft: 6 }} />
          </Link>
        </FreshDropsHead>
        <FreshDropsGrid>
          {[
            { img: jl1, n: 1, to: '/shop/women' },
            { img: jl2, n: 2, to: '/shop/men' },
            { img: jl3, n: 3, to: '/shop/women' },
            { img: jl4, n: 4, to: '/shop/men' },
            { img: jl5, n: 5, to: '/shop/women' },
            { img: jl6, n: 6, to: '/shop/men' },
            { img: jl7, n: 7, to: '/shop/women' },
            { img: jl8, n: 8, to: '/shop/men' },
          ].map((j) => (
            <FreshDropCard key={j.n} to={j.to}>
              <FreshDropImg src={j.img} alt={t(`modamai.home.fresh${j.n}Title`)} loading="lazy" />
              <FreshDropOverlay />
              <FreshDropCopy>
                <FreshDropEyebrow>{t(`modamai.home.fresh${j.n}Eyebrow`)}</FreshDropEyebrow>
                <FreshDropTitle>{t(`modamai.home.fresh${j.n}Title`)}</FreshDropTitle>
              </FreshDropCopy>
            </FreshDropCard>
          ))}
        </FreshDropsGrid>
      </FreshDropsSection>

      <section style={{ padding: '4rem 0 2.25rem' }}>
        <ProductCatalogWide>
          <div style={{ textAlign: 'left', marginBottom: '2.5rem' }}>
            <SectionTitle style={{ margin: 0 }}>{t('modamai.home.mostWantedTitle')}</SectionTitle>
            <SectionKicker style={{ color: '#524D46' }}>{t('modamai.home.mostWantedSub')}</SectionKicker>
            {apiOn && homeLoading && gridProducts.length === 0 && (
              <p style={{ margin: '0.75rem 0 0', textAlign: 'left', fontSize: 13, color: '#7a7268' }}>
                {t('modamai.home.loading')}
              </p>
            )}
          </div>
          <ProductCardsGrid>
            {gridProducts.map((p) => (
              <ProductCard key={`${p.slug || p.id}-grid`} product={p} />
            ))}
          </ProductCardsGrid>
          <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
            <Button as={Link} to="/shop/new" $variant="luxe" $size="lg">
              {t('modamai.home.shopAllNewIn')}
            </Button>
          </div>
        </ProductCatalogWide>
      </section>

      <HomeReviewsSection />
    </div>
  );
}
